import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogShell,
  DialogShellHeader,
  DialogShellBody,
  DialogShellFooter,
  AssigneeDisplay
} from '@bliks-io/ui';
import type { EntitySuggestion } from '@bliks-io/contracts';
import { CircleHelp, PencilLine, UserPlus } from 'lucide-react';
import { useProcess } from '@/api/queries/useProcesses';
import { SuccessorEntityPickerModal } from '@/components/entity-pickers/successor-picker/SuccessorEntityPickerModal';
import {
  useCreatedSuggestionSession,
  type CreatedSuggestionSession
} from '@/components/entity-pickers/hooks/useCreatedSuggestionSession';
import type { SuggestionPickEvent } from '@/components/entity-pickers/shared/suggestionPickEvent';
import { useAssigneeLookup } from '@/lib/useAssigneeLookup';
import {
  isUnclearStatus,
  pickReassigneeFromSuccessors,
  toReassignPayload,
  type ReassignPayload,
  type ReassigneePair
} from '@/lib/successorEntityMapping';
import type { SuccessorUpdateData } from '@/pages/processes/recording/types';

// Shared shell for the clickable staged-selection chips (unclear / resolved).
// One string so a style tweak can't drift between the variants.
const CHIP_BUTTON_CLASS =
  'flex w-full items-center gap-3 rounded-md border bg-muted/30 px-3 py-2 text-left transition-colors hover:border-primary/50 hover:bg-accent disabled:opacity-60';

interface ReassignContextDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: SuccessorUpdateData) => void;
  /**
   * Created-suggestion ledger owned by an enclosing flow. Flows with a
   * downstream confirm of their own (the recording reassign) pass their
   * session so suggestions created here stay cancellable until THAT confirm —
   * the dialog then tracks into it instead of keeping a ledger of its own
   * (one ledger, one withdraw site). Direct-submit consumers omit it and the
   * dialog owns the session itself.
   */
  session?: CreatedSuggestionSession;
  title: string;
  description?: string;
  /** Context section rendered above the picker trigger (alerts, user cards, step preview…).
   *  Pass a function to re-render against the staged picker selection (live preview). */
  contextBody?: ReactNode | ((staged: SuccessorUpdateData | null) => ReactNode);
  /** Forwarded to the picker — needed for picker suggestion APIs. */
  processId?: string;
  recordingId?: string;
  /**
   * Override for the suggestion lookup behind the staged chip's blue proposed
   * names. Defaults to the process's own suggestions (fetched here); pass it
   * only when the flow holds a richer list (the recording reassign merges its
   * session's creates in).
   */
  entitySuggestions?: EntitySuggestion[];
  /** Hide the "unclear" row in the picker (use for resolve-unclear flows). */
  hideUnclear?: boolean;
  /**
   * Hide the "external contact" row in the picker. Set when reassigning a
   * start-event step: external contacts can't perform a process's start
   * activity, so they're never offered there (initial add or reassignment).
   */
  hideExternalContact?: boolean;
  isSubmitting?: boolean;
}

/**
 * Two-step wrapper for reassign / nominate / resolve-unclear flows:
 *
 *   1. Context dialog — preserves the situational detail (alerts, who marked
 *      this unclear, step preview) the legacy AssignmentModal used to show.
 *   2. Picker — opened via the in-context "Choose assignee" button; on save
 *      we stage the selection back into the context dialog so the user can
 *      review before submitting.
 *
 * Suggest-new inside the picker creates the `EntitySuggestion` eagerly (same
 * path as the editor/recording pickers); the staged selection then carries a
 * `{ type: 'suggestion', id }` ref. Cancelling fires the guarded withdraw for
 * every suggestion created in the session; confirming fires it for every one
 * except the confirmed selection — the backend deletes
 * only what is still pending and unreferenced, so anything already adopted
 * elsewhere survives.
 *
 * "None / ending" is always hidden — these flows redirect work in progress.
 * "Unclear" stays visible by default (reassign / nominate keep the mark-
 * unclear path); set `hideUnclear` for resolve-unclear.
 */
export function ReassignContextDialog({
  open,
  onOpenChange,
  onSubmit,
  session: parentSession,
  title,
  description,
  contextBody,
  processId,
  recordingId,
  entitySuggestions,
  hideUnclear,
  hideExternalContact,
  isSubmitting
}: ReassignContextDialogProps) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Flows without a recording of their own (start-anchored reassigns,
  // resolve-unclear) anchor suggest-new on the process's start event node —
  // the unified stand-in for the retired process-level anchor. Derived here
  // so every consumer gets the fallback; recording-backed flows anchor on
  // the recording, so the node is withheld to keep exactly one anchor in
  // play. The same fetch supplies the default suggestion lookup. The hook
  // is disabled on '' (dialog closed or no process).
  const { data: processData } = useProcess(open ? (processId ?? '') : '');
  const startEventNodeId = recordingId
    ? undefined
    : processData?.events?.find((event) => event.eventKind === 'start')?._id;
  const suggestionSource = entitySuggestions ?? processData?.entitySuggestions;
  const [staged, setStaged] = useState<SuccessorUpdateData | null>(null);
  // The single created-suggestion ledger: the parent flow's session when one
  // is provided, otherwise dialog-owned (the hook must run unconditionally;
  // it just stays empty when a parent session is in charge).
  const ownSession = useCreatedSuggestionSession();
  const session = parentSession ?? ownSession;

  // Reset on every open instead of on close: consumers close programmatically
  // after a successful submit (parent nulls its selection), which flips `open`
  // without ever calling onOpenChange — resetting there would leak the last
  // staged chip into the next dialog. Same prevOpenRef idiom as
  // useCreatedIdsMerge.
  const prevOpenRef = useRef(open);
  if (prevOpenRef.current !== open) {
    prevOpenRef.current = open;
    if (open) {
      setStaged(null);
      setPickerOpen(false);
      // Only the dialog-owned ledger resets here: after a programmatic close
      // the previous session's suggestion has a persisted ref, so the next
      // session must stop tracking it (its cancel must not withdraw it). A
      // parent session's lifecycle belongs to the flow — its confirm clears,
      // its cancel withdraws — and render-phase setState may only target this
      // component's own state anyway.
      ownSession.clear();
    }
  }

  const handlePickerSave = (data: SuccessorUpdateData) => {
    setStaged(data);
    setPickerOpen(false);
  };

  // Only brand-new creates enter the ledger: an adopted suggestion predates
  // this dialog and must never be withdrawn by it.
  const handleSuggestionPick = (event: SuggestionPickEvent) => {
    if (event.kind === 'created') session.track(event.suggestion);
  };

  // User-initiated close (cancel / X / esc). Withdraw everything created in
  // this session, fire-and-forget: nothing referenced the suggestions yet (the
  // submit that would persist a ref didn't happen), and the backend guard
  // keeps anything another flow adopted meanwhile. The submit path closes via
  // the `open` prop instead, so confirmed suggestions are never withdrawn here.
  const handleUserClose = () => {
    // While a direct-submit consumer's reassign POST is in flight, the server
    // is about to persist the staged suggestion ref — a withdraw fired now
    // would race that write and could delete the referenced suggestion. The
    // footer buttons are already disabled during submit, but Radix esc /
    // overlay and the header X are not, so refuse the close here too.
    if (isSubmitting) return;
    session.withdrawAll();
    onOpenChange(false);
  };

  // Suggestions created in this dialog but replaced before confirm were never
  // bound, so withdraw them now; the confirmed one stays tracked until its
  // flow settles.
  const handleConfirm = () => {
    if (!staged) return;
    session.withdrawUnreferenced(confirmedSuggestionIds(toReassignPayload(staged)));
    onSubmit(staged);
  };

  // The created docs aren't in the suggestion source yet — that list only
  // carries suggestions already referenced server-side, and the staged ref
  // lives in client state until submit — so merge them for the chip lookup.
  const suggestionLookup = useMemo(
    () => session.mergeInto(suggestionSource),
    [session, suggestionSource]
  );

  return (
    <>
      <Dialog open={open && !pickerOpen} onOpenChange={(next) => !next && handleUserClose()}>
        <DialogShell size="lg">
          <DialogShellHeader title={title} subtitle={description} onClose={handleUserClose} />
          <DialogShellBody>
            {typeof contextBody === 'function' ? contextBody(staged) : contextBody}

            <div className="space-y-2">
              <h3 className="text-sm font-medium">{t('reassignContext.assignToHeading')}</h3>
              <StagedSelectionButton
                staged={staged}
                entitySuggestions={suggestionLookup}
                disabled={isSubmitting}
                onClick={() => setPickerOpen(true)}
              />
            </div>
          </DialogShellBody>
          <DialogShellFooter
            secondary={{
              label: t('common.cancel'),
              onClick: handleUserClose,
              disabled: isSubmitting
            }}
            primary={{
              label: t('reassignContext.confirm'),
              onClick: handleConfirm,
              disabled: !staged || isSubmitting,
              loading: isSubmitting
            }}
          />
        </DialogShell>
      </Dialog>

      <SuccessorEntityPickerModal
        open={pickerOpen}
        mode="reassign"
        onOpenChange={(next) => setPickerOpen(next)}
        onSave={handlePickerSave}
        onSuggestionCreated={handleSuggestionPick}
        processId={processId}
        recordingId={recordingId}
        startEventNodeId={startEventNodeId}
        pendingSuggestions={suggestionLookup}
        hideNone
        hideUnclear={hideUnclear}
        hideExternalContact={hideExternalContact}
      />
    </>
  );
}

function confirmedSuggestionIds(payload: ReassignPayload | null): string[] {
  return payload?.reAssigneeType === 'suggestion' && payload.reAssigneeId
    ? [payload.reAssigneeId]
    : [];
}

interface StagedSelectionButtonProps {
  staged: SuccessorUpdateData | null;
  entitySuggestions?: EntitySuggestion[];
  disabled?: boolean;
  onClick: () => void;
}

function StagedSelectionButton({
  staged,
  entitySuggestions,
  disabled,
  onClick
}: StagedSelectionButtonProps) {
  const { t } = useTranslation();

  if (staged && isUnclearStatus(staged)) {
    return (
      <button type="button" onClick={onClick} disabled={disabled} className={CHIP_BUTTON_CLASS}>
        <CircleHelp className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="flex-1 text-sm">{t('reassignContext.markedUnclear')}</span>
        <PencilLine className="h-4 w-4 text-muted-foreground shrink-0" />
      </button>
    );
  }

  const pair = staged ? pickReassigneeFromSuccessors(staged) : null;
  if (pair) {
    // Lazily mount the chip — its lookup queries only fire once the user has
    // picked an entity, not on every modal open. A suggestion ref resolves to
    // its blue proposed name via `entitySuggestions`.
    return (
      <ResolvedAssigneeChip
        pair={pair}
        entitySuggestions={entitySuggestions}
        disabled={disabled}
        onClick={onClick}
      />
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      disabled={disabled}
      className="w-full justify-start"
    >
      <UserPlus className="h-4 w-4 mr-2" />
      {t('reassignContext.chooseAssignee')}
    </Button>
  );
}

interface ResolvedAssigneeChipProps {
  pair: ReassigneePair;
  entitySuggestions?: EntitySuggestion[];
  disabled?: boolean;
  onClick: () => void;
}

function ResolvedAssigneeChip({
  pair,
  entitySuggestions,
  disabled,
  onClick
}: ResolvedAssigneeChipProps) {
  const { t } = useTranslation();
  const assigneeLookup = useAssigneeLookup();

  return (
    <button type="button" onClick={onClick} disabled={disabled} className={CHIP_BUTTON_CLASS}>
      <div className="flex-1 min-w-0">
        <AssigneeDisplay
          assignedTo={{ type: pair.type, id: pair.assigneeId }}
          assignedToModel={pair.assigneeModel}
          entitySuggestions={entitySuggestions}
          assigneeLookup={assigneeLookup}
          t={t}
        />
      </div>
      <PencilLine className="h-4 w-4 text-muted-foreground shrink-0" />
    </button>
  );
}
